import { or, and, not, createMachine } from 'xstate';

import { routePath } from 'config/route-config';
import { actionUrl } from 'config/static-content';
import { events } from 'machines/config/events';
import { guards } from 'machines/config/guards';
import { stringToBool } from 'utils/misc';

export const directSalesMachineConfig = {
  id: 'directSalesMachineRevamp',
  context: {
    guards: {}
  },
  initial: 'home',
  states: {
    home: {
      on: {
        NEXT: [
          {
            target: 'energyNeeds',
            guard: 'hasAnyMeterValidationRequired'
          },
          {
            target: 'selectOptions',
            description: `or(['hasMeterExceptions', 'hasMixedMeters'])`,
            guard: or(['hasMeterExceptions', 'hasMixedMeters'])
          },
          {
            target: 'energyNeeds'
          }
        ],

        BLOCK_JOURNEY: {
          target: 'businessUpdatesAnnouncement'
        }
      },
      meta: {
        path: routePath.home
      }
    },

    businessUpdatesAnnouncement: {
      on: {
        NEXT: {
          target: 'businessUpdatesSubmitEmail'
        },
        ALLOW_JOURNEY: {
          target: 'home'
        }
      },

      meta: {
        path: routePath.businessUpdatesAnnouncement
      }
    },

    businessUpdatesSubmitEmail: {
      on: {
        NEXT: {
          target: 'businessUpdatesSubmitEmailSuccess'
        }
      },
      meta: {
        path: routePath.businessUpdatesSubmitEmail
      }
    },

    businessUpdatesSubmitEmailSuccess: {
      meta: {
        path: routePath.businessUpdatesSubmitEmailSuccess
      }
    },

    meterpointValidation: {
      on: {
        NEXT: [
          {
            target: 'selectOptions',
            description: `or(['hasMeterExceptions', 'hasMixedMeters'])`,
            guard: or(['hasMeterExceptions', 'hasMixedMeters'])
          },
          {
            target: 'energyNeeds'
          }
        ],
        GUIDE_CLICK: {
          target: 'meterPointGuide'
        }
      },
      meta: {
        path: routePath.meterpointValidation
      }
    },

    meterPointGuide: {
      meta: {
        path: routePath.meterpointGuide
      }
    },

    selectOptions: {
      on: {
        ELIGIBLE_FUEL: {
          target: 'energyNeeds'
        },
        EDIT_ADDRESS: {
          target: 'home'
        },
        HOME_MOVE: [
          {
            target: 'externalContactUs',
            description: `or([
              'isMixedNepSap',
              and([not('allowJourneyMoveIn'), 'isBGECustomer'])
            ])`,

            guard: or([
              'isMixedNepSap',
              and([not('allowJourneyMoveIn'), 'isBGECustomer'])
            ])
          },
          {
            target: 'externalMoveIn',
            guard: 'isBGECustomer'
          },
          {
            target: 'externalBgClassicHomeMove'
          }
        ],
        MIXED_METERS: {
          target: 'energyNeeds'
        }
      },
      meta: {
        path: routePath.selectOptions
      }
    },

    energyNeeds: {
      on: {
        NEXT: [
          {
            target: 'meterpointValidation',
            guard: 'meterValidationRequired'
          },
          {
            target: 'selectOptions',
            description: `or(['hasMeterExceptions', 'hasMixedMeters'])`,
            guard: or(['hasMeterExceptions', 'hasMixedMeters'])
          },
          {
            target: 'evTariffInformation',
            guard: 'showEvTariffInformation'
          },
          {
            target: 'emailCapture',
            description: "and(['releaseAbandonedCart', 'isEligibleCustomer'])",
            guard: and(['releaseAbandonedCart', 'isEligibleCustomer'])
          },
          {
            target: 'quote',
            guard: 'isEligibleCustomer'
          },
          {
            target: 'personalInformationSummary'
          }
        ]
      },
      meta: {
        path: routePath.energyNeeds
      }
    },

    evTariffInformation: {
      on: {
        NEXT: [
          {
            target: 'emailCapture',
            description: "and(['releaseAbandonedCart', 'isEligibleCustomer'])",
            guard: and(['releaseAbandonedCart', 'isEligibleCustomer'])
          },
          {
            target: 'quote',
            guard: 'isEligibleCustomer'
          },
          {
            target: 'personalInformationSummary'
          }
        ]
      },
      meta: {
        path: routePath.evTariffInformation
      }
    },

    personalInformationSummary: {
      on: {
        NEXT: {
          target: 'externalBgSalesApp'
        },
        CHANGE_ADDRESS: {
          target: 'home'
        },
        CHANGE_ENERGY_NEEDS: {
          target: 'energyNeeds'
        }
      },
      meta: {
        path: routePath.personalInformationSummary
      }
    },

    emailCapture: {
      on: {
        NEXT: [
          {
            target: 'quote',
            guard: 'isEligibleCustomer'
          },
          {
            target: 'personalInformationSummary'
          }
        ]
      },
      meta: {
        path: routePath.emailCapture
      }
    },

    quote: {
      on: {
        NEXT: {
          target: 'personalDetails'
        },
        EMAIL_QUOTE: {
          target: 'emailQuote'
        },
        CONSUMPTION: {
          target: 'energyNeeds'
        }
      },
      meta: {
        path: routePath.quote
      }
    },

    emailQuote: {
      on: {
        NEXT: {
          target: 'quote'
        }
      },
      meta: {
        path: routePath.emailQuote
      }
    },

    personalDetails: {
      on: {
        NEXT: [
          {
            target: 'priorityServicesRegister',
            guard: 'hasPSR'
          },
          {
            target: 'directDebit',
            guard: 'isAnyDirectDebit'
          },
          {
            target: 'orderSummary'
          }
        ]
      },
      meta: {
        path: routePath.personalDetails
      }
    },

    priorityServicesRegister: {
      on: {
        NEXT: [
          {
            target: 'directDebit',
            guard: 'isAnyDirectDebit'
          },
          {
            target: 'orderSummary'
          }
        ]
      },
      meta: {
        path: routePath.psrEntry
      }
    },

    directDebit: {
      on: {
        NEXT: {
          target: 'orderSummary'
        }
      },

      meta: {
        path: routePath.directDebit
      }
    },

    orderSummary: {
      on: {
        NEXT: {
          target: 'orderComplete'
        },
        CHANGE_PERSONAL_DETAILS: {
          target: 'personalDetails'
        },
        CHANGE_ADDRESS: {
          target: 'home'
        },
        CHANGE_PAYMENT_DETAILS: {
          target: 'directDebit'
        },
        CHANGE_PSR_DETAILS: {
          target: 'priorityServicesRegister'
        },
        CHANGE_ENERGY_NEEDS: {
          target: 'energyNeeds'
        }
      },
      meta: {
        path: routePath.orderSummary
      }
    },

    orderComplete: {
      meta: {
        path: routePath.orderComplete
      }
    },

    externalBgSalesApp: {
      meta: {
        path: actionUrl.bgSalesApp
      }
    },

    externalContactUs: {
      meta: { path: actionUrl.contact }
    },
    externalBgClassicHomeMove: {
      meta: { path: actionUrl.bgClassicHomeMoveUrl }
    },

    externalMoveIn: {
      meta: { path: actionUrl.moveIn }
    }
  },
  ...events
};

const { VITE_XSTATE_DEBUG } = process.env;
const debug = stringToBool(VITE_XSTATE_DEBUG);
// for VS extesnion
/* istanbul ignore next -- @preserve */
debug &&
  createMachine(
    {
      /** @xstate-layout N4IgpgJg5mDOIC5QQJYCcwGMAuBlAhgDZwCy+mAFigHZgBKYAbvgLYAOAdBQPYtgDEAOQCiADQAqAbQAMAXUSg23WCmwpu1BSAAeiAGwBOAKwcD0gIwAOACyW9e89IDMTgwBoQAT0TmnAdksOawMAJgN-P0jzaz0QgF84j1QMHAJiWDJKGnomVk4ePiExKXN5JBAlFTUNLV0EQxMzK1t7Rxd3L31pEyMLIxCjQcMzPwSk9Cw8IlJyKloGZnYuXgERCUkQssVlVXVNcrqG0wsbOwdnVw9vBGiQwKdm81C9M6NLMZBkybSZrPncpYFAQAIQAMgB5ADCAGkAPoAKXBAFU6CIAJoyLYVHbVfagQ7GY7NM5tS6dBD+cymPz+frdAx+PTSbofL6paYZWbZBZ5DgAIwAripaLBYEi2BB8Ng4ABBajUbgC6iYMB8ajYIrrORaSq7GoHHxOfpBB5GazSAbmF5GPRXRAuaQcEJhSx+cxGcx3RlOaysibs9KZOY5RacQXCuBiiVS2XyxXK1VgdX8GWgiEAdQRyNRwgx2vKutxtUQNka5ppBldRlCHrtFOdHGilhCfm6LxC5t9iU+-qmga5-1D-KF2VF4sl0tguAFfJYqmELHwKEImqk+e2VT2xYQIQuHD85v8tLe4Vt5IdTpdbo9Xr0Pr9KT7v2DPKWfGlaCUNGwADUiCgJz2VdMR1HEtwNHc9wPZwaT8QZLFPOt7CcDh+gMBwXTeA8DAfb4OSDbkAU4d8wE-bhvz-QgAKlIC1hKLFC3A-FEBCd0UL0eDYgGaRQicOtHDMDgfSeA8-AMaxHBpXCA2fQihxIsiKP-QCNH4ABxJEAEkABFhFhSFQU0mEQILMD9WYndWxCfcEI4j0bR4s9rgE6ybBE6wxIk6QpO7Nkn05P4Q15WAwGIHBwTYXFYH4YRDLUzSwT0gAxJFYpMjc9TxHRDWNaxTXNS1rScxAxIMDhDEccwrCq6QXicaT-IIwdgtCyYIqimLtM08RYRlbTtLoYRcFwdLsU3czsoQOw-CE6InAsSJLCcOxrDrQYTD0RlpGsc0OI83wGp+AKXyIjgQrC7B2r2aKAAlwRIPSSHBH9hFGxiJrqXxcvyi0PSKutlsdbojCNebttcN5DvwgcgqWc62si67+Duh7YSel7JFKUDxqyz6jWsvL3QKv7LBtNbmSEuDQhiD0aQhqH+0C19OHh8LEY0W77se57Xs2bHMu3L6CZ+wrSeKhB4I4OxBnmh5jFsd5fN7I6mthlnWrZjqSE00RhG0tHhHEYQ6BG9cxoFiChZNInfqtMW6xsMq-uCB0BldUYlcfFWYeZjgk1IqBPEEMBIGiui3rM3GWKgw9YPgxDySq81G026wbTudPNoZ2TmqWf20ED4PQ+ArHTJx7ddxcfdY+PBCnHF91vKl3cMMsZkq3qz28MZk6h3zwuQ4gMPig2BjI4rmOYNrhPnIPQI9BiVxnC+k9s+OuTeX7oPB+H9YnDH8uIMrlDoKPOCT3r-jAdMUJwkiKIYniLuZPX3POCYcR8DQFAADMf806gP9uBoEXLiYCZt3pR0shaGy6FBjuiZOhfi8CnQPCtMyaI1gBhGDXqrX2H8v6-3-oA4BoDaIj1LhlIsR8rKwLsggxyyCLBBDbAEfoYRaq4J9qdNgpFYAaCIAAoBICaIaGnCwRcaBPDgIPhbCyTgQiGA4BYFwIR5oDC4nWSsKEPIOHdHPCqOFn6NW4UOXhaB+HUEESQkRuJxGSOkZCG6MpBBqT0n1AaQ1TayOoRZFsd4paMlbLYG0cEQhaKUbfNRnYloeU7uML20MmY8L4QIwgQjSGiOoPYr+jjnGuL0pCcEghcBIhIAABXEJpYpEdD5+L8LufcrYbBqOiHtBuijHRvAsAyZey0F5cOSUOAAjgKbg0oZH818ZNBRSiVEKPUencJicHAoXMGJbaYRnQIUsIrBJ3cc5qw4KM8ZAhhAkBlJpUEsIACKSJwRG1qXImZiiyrzLUb9TRicbQoVBjaOavhPKDN7ryE5EyiklLKZU6pggnnTLqC2AwVJQjRI8madClh+KxECGo+aRgwlmjdE-fZL88GnVVEuQgNyxkTPDhA8eEEXBV3Es6FsVUKwdGcnuXZEldz7VdOYYFG8ljmMsUQbSYBsCUt3muHxTEZl3hxew+uVVxJ+CxY0rBNJHAgywRYIVb8OBsG-sA1QnhcCkUYCgFUsAGBQBQLAD8kyy7PLqK4N0ToQbYukDyt0dZdyBG0WJA8LgAgPA9iSkxQzeR+QlXyVQzqqHyrdQyKkAx653B9TYa8DsqQKJbAhbym0A04OMd7aNSxgEQFIjkqRibzbwpYmEJ0jJxJL2ZLLIwWKXjUmrNi2wqi9k9kST3YVnAq01oFBI3J-AnEuLcbCcpxtcDFNTLCXS4hLmgm8VM5NTayr+LbWYDtzgu2JzsG86svgdXhCsHoA1RyJ1oFrXk+d7j+qDWGnCvdO5m2Hryse5wp6kJWH3L0Kw9d65YJ2g+32T6X2zvyQu8pMo0QPUED1DdW6d0usbb+g9raAPMiA0afiSLAg8owoMH1y1YOnXg1OhxiG32LtwHQddhtsPfo+vulthgiMntI+SXcVIaSDBdM6Ww3QI3DoOa-R9aBq3PsYzOudBTYTCBEHQNSaJYQiD1jhpNPH8N8aPcRztyDbD7jQUyCwO1sEJG7Aqat8Byh+XLSC9gu7jMAFpxZ+eUcRoLwXvJ0aHECbzUCbQ4oaN0T0pNgjLOuA4QNjw4L8fLGF3k4ZRxRgnLGBUSoVRqmwJFwW21HQVjaXeFRZNzzzSEgyNuLKPJ3HvWWpJnmwwjhFHlmMU4ZxzmwAuSlZXLYHmsulpagKGkLzPdcC87rmvQYaXYLLSwcu9fHP16cs55yLmXNOTANrXNGagWo1sTo7zIWZHeXwq16uOiWzxFbbX1vEUlaRL86pKLUWmZAiu5ounVgQmYcS2L1WJx4ihZkBgkUtnpH297HAFLlPIuqNSAoAJgDG34uHFHGRmj6PNA8837QNibAj6LHZtrI9Zpddm1BTsNp-VVVwl4PQuCWkyWIkPrg-NMNg5soQnjVi7JGjzY6-a0ALtvUOuPJpWB4mB+wzploBo8lfAI5UmRVheKDmwyOCHfz-hk2x8qAdHxiFSXXCF4FQc198pulZSa9CJQ0xkyPRVpLN2QsRKmpEK4RfjwJe1ifeTTlopa5UdrdHrkyM0WDkdgpx7hn981o-LUsE8STxgwh8W+Q4ZRrFgjhDyq0mT7nOtS4pcualpyg-2jynoazpejQ2jhzSfimzGwuA4uEO4RojES+r4a73VjCASqlcuZnlv5E7Rb2nD03lBi305T4WIXS8+2AX3H9rI-R1j5Nd-bA5rLXWrgHah1H5G8UgPFSbavRfrVlqqTf1yv3TrNBmnO8ifkexrAHjVKzT2M3NBsGUUXnEn6Dbj50QHCHKmvTuHNHE2tGRwY2nUDxAKgSwUsxb1cDBzEkURbACDQMU1IkhF4DYGIGlFvxvCCAaS-1USGExRWSUTmmPS2isFLQP0OXwW0A-An2BCgCOhlDYDYFvx9Hrlbzh3b2GC73JG0Rj2QPj26HsyN34NIgnwoPVHIGwCRFnwZQsnWSwRV2mwiEUUj3JAkhMA8ho2rHEn0W4Nk1JVMU3g0LQEEKgEhEIHwFFGtRuhWBIG4EYFTzO0Fgm1MIeHMLmzWmrGURcBiGbAeHNE9HUIEKICCJCIAVv2MMm2iyiMiAsLJwlgpjNH6Q0SNDsHvEcyAA */ ...directSalesMachineConfig
    },
    {
      guards
    }
  );
